import BackendAuthHandler from 'BackendAuthHandler';
import { supportLoggingHeaders } from '../supportLoggingStore';
import { UNABLE_TO_AUTHENTICATE } from './constants';
import { parseStructuredError } from './structuredErrors';
import type { PromotionPrerequisiteIssue, StructuredError } from './types';

export class BaseBackendServiceHttp {
  public apiURL(path: string): string {
    const trimmed = path.startsWith('/') ? path.slice(1) : path;
    return `/api/${trimmed}`;
  }

  protected authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = {
      // A FormData body must keep the browser-generated multipart Content-Type
      // (with its boundary) — forcing application/json breaks the upload.
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...supportLoggingHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    };
    if (isFormData) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
    const auth = headers['Authorization'] ?? headers['authorization'];
    if (auth === 'Bearer null' || auth === 'Bearer undefined' || auth === 'Bearer ') {
      delete headers['Authorization'];
      delete headers['authorization'];
    }
    return fetch(url, {
      credentials: 'include',
      ...options,
      headers,
    });
  }

  protected authFetchPromise<T>(run: (token: string | null) => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      new BackendAuthHandler()
        .withAuthToken()
        .then((token) => run(token).then(resolve).catch(reject))
        .catch(reject);
    });
  }

  protected authPostJson<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `HTTP status ${response.status}`);
      }
      return response.json();
    });
  }

  protected authGetJson<T>(path: string): Promise<T> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(path), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw errorBody?.error ? new Error(errorBody.error) : errorBody;
      }
      return response.json();
    });
  }

  protected authApiGet<T>(path: string, map: (json: any) => T = (json) => json as T): Promise<T> {
    return this.authGetJson<any>(path).then(map);
  }

  protected authApiPost<T>(
    path: string,
    body?: unknown,
    map: (json: any) => T = (json) => json as T,
  ): Promise<T> {
    return this.authPostJson<any>(
      path,
      body === undefined ? undefined : (body as Record<string, unknown>),
    ).then(map);
  }

  protected authApiGetData<T>(path: string, networkErrorMessage: string): Promise<T> {
    return this.authFetchPromise(async () => {
      const response = await this.authenticatedFetch(this.apiURL(path), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      return ((await response.json()) as { data: T }).data;
    }).catch((error) => {
      if (error?.code && error?.error) {
        throw error;
      }
      throw this.handleError({ code: 'NETWORK_ERROR', error: networkErrorMessage });
    });
  }

  protected authApiPostData<T>(
    path: string,
    body: unknown,
    networkErrorMessage: string,
    requireAuth = false,
  ): Promise<T> {
    return this.authFetchPromise(async (token) => {
      if (requireAuth && !token) {
        throw 'Authentication required';
      }

      const response = await this.authenticatedFetch(this.apiURL(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      return ((await response.json()) as { data: T }).data;
    }).catch((error) => {
      if (typeof error === 'string') {
        throw error;
      }
      if (error?.code && error?.error) {
        throw error;
      }
      throw this.handleError({ code: 'NETWORK_ERROR', error: networkErrorMessage });
    });
  }

  protected authApiGetAuthed<T>(
    path: string,
    networkErrorMessage: string,
    map: (json: any) => T = (json) => json.data as T,
    absoluteUrl = false,
  ): Promise<T> {
    return this.authFetchPromise(async (token) => {
      const url = absoluteUrl ? path : this.apiURL(path);
      const response = await this.authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        try {
          throw this.handleError(await response.json());
        } catch (error) {
          if (error && typeof error === 'object' && 'structuredError' in (error as object)) {
            throw error;
          }
          throw this.handleError({ code: 'UNKNOWN_ERROR', error: networkErrorMessage });
        }
      }
      return map(await response.json());
    }).catch((error) => {
      if (error && typeof error === 'object' && 'structuredError' in error) {
        throw error;
      }
      throw this.handleError({ code: 'NETWORK_ERROR', error: networkErrorMessage });
    });
  }

  protected authApiMutateAuthed<T>(
    path: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: unknown,
    map: (json: any) => T = (json) => json as T,
    absoluteUrl = false,
  ): Promise<T> {
    return this.authFetchPromise(async (token) => {
      const url = absoluteUrl ? path : this.apiURL(path);
      const response = await this.authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.code && errorData?.error) {
          throw this.handleError(errorData);
        }
        throw errorData?.error || `Error: ${response.status}`;
      }
      if (response.status === 204) {
        return undefined as T;
      }
      return map(await response.json());
    });
  }

  protected authApiPatchVoid(path: string): Promise<void> {
    return this.authApiMutateAuthed<void>(path, 'PATCH', undefined, () => undefined);
  }

  protected authApiDeleteVoid(path: string, absoluteUrl = false): Promise<void> {
    return this.authApiMutateAuthed<void>(path, 'DELETE', undefined, () => undefined, absoluteUrl);
  }

  protected xhrUpload<T = void>(
    method: 'PUT' | 'POST',
    url: string,
    body: Blob | FormData,
    options?: {
      contentType?: string;
      headers?: Record<string, string>;
      onUploadProgress?: (progress: number) => void;
      mapResponse?: (xhr: XMLHttpRequest) => T;
    },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (options?.onUploadProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            options.onUploadProgress!((event.loaded / event.total) * 100);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(options?.mapResponse ? options.mapResponse(xhr) : (undefined as T));
          } catch (error) {
            reject(error);
          }
          return;
        }
        try {
          const errorData = JSON.parse(xhr.responseText);
          reject(new Error(errorData.error || `Error: ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open(method, url);
      if (options?.headers) {
        for (const [key, value] of Object.entries(options.headers)) {
          xhr.setRequestHeader(key, value);
        }
      }
      if (options?.contentType) {
        xhr.setRequestHeader('Content-Type', options.contentType);
      }
      xhr.send(body);
    });
  }

  protected putFileViaXhr(
    uploadUrl: string,
    file: File | Blob,
    contentType?: string,
    onUploadProgress?: (progress: number) => void,
  ): Promise<void> {
    return this.xhrUpload('PUT', uploadUrl, file, { contentType, onUploadProgress });
  }

  protected async handleAuthRedirect(response: Response): Promise<boolean> {
    if (response.status !== 401) {
      return false;
    }
    const data = await response.json();
    const authUrl = data.authUrl;
    if (authUrl) {
      localStorage.setItem('authRedirect', window.location.pathname);
      window.location.href = authUrl;
      return true;
    }
    throw UNABLE_TO_AUTHENTICATE;
  }

  protected async verifySignedConfigData(
    data: unknown,
    signature: string,
    publicKeyBase64: string,
  ): Promise<boolean> {
    const rsaAlgorithm = 'RSASSA-PKCS1-v1_5';
    try {
      const enc = new TextEncoder();
      const keyData = Uint8Array.from(atob(publicKeyBase64), (c) => c.charCodeAt(0));
      const sigData = Uint8Array.from(atob(signature), (c) => c.charCodeAt(0));
      const key = await crypto.subtle.importKey(
        'spki',
        keyData,
        { name: rsaAlgorithm, hash: 'SHA-256' },
        false,
        ['verify'],
      );
      return crypto.subtle.verify(rsaAlgorithm, key, sigData, enc.encode(JSON.stringify(data)));
    } catch (error: unknown) {
      console.error(error);
      return false;
    }
  }

  handleError(error: {
    code: string;
    error: string;
    issues?: PromotionPrerequisiteIssue[];
  }): StructuredError {
    return parseStructuredError(error);
  }
}
